import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Typography,
  Avatar,
  Stack,
  Chip,
  Collapse,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LogoutIcon from '@mui/icons-material/Logout';
import { SpotifyIcon } from '@/components/icons/SpotifyIcon';
import { GoogleIcon } from '@/components/icons/GoogleIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useLayout } from '@/contexts/LayoutContext';
import { analytics } from '@/lib/analytics';

const DISMISSED_KEY = 'sso-footer-dismissed';

export function SSOFooter() {
  const {
    spotifyToken,
    spotifyUser,
    spotifyLogin,
    spotifyLogout,
    spotifyLoading,
    googleUser,
    googleLogin,
    googleLogout,
    googleLoading,
    isHydrated,
  } = useAuth();

  const { playbackFooterHeight } = useLayout();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [expanded, setExpanded] = useState(!isMobile);

  useEffect(() => {
    function handleShow() {
      setDismissed(false);
      setExpanded(true);
      try { sessionStorage.removeItem(DISMISSED_KEY); } catch { /* ignore */ }
    }
    window.addEventListener('show-connections', handleShow);
    return () => window.removeEventListener('show-connections', handleShow);
  }, []);

  if (typeof window === 'undefined' || !isHydrated) return null;

  if (dismissed) return null;

  const bottomOffset = playbackFooterHeight > 0 ? playbackFooterHeight + 16 : 16;

  const handleDismiss = () => {
    setDismissed(true);
    analytics.ssoFooterDismissed();
    try {
      sessionStorage.setItem(DISMISSED_KEY, 'true');
    } catch { /* ignore */ }
  };

  if (!expanded) {
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: bottomOffset,
          right: 16,
          zIndex: 1100,
          borderRadius: '50%',
          transition: 'bottom 0.3s ease',
          WebkitTransform: 'translateZ(0)',
          touchAction: 'manipulation',
        }}
      >
        <Tooltip title="Sign-in options" disableTouchListener>
          <IconButton
            disableRipple
            onClick={() => { setExpanded(true); analytics.ssoFooterExpanded(); }}
            sx={{
              width: 48,
              height: 48,
              bgcolor: 'background.paper',
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            <ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />
          </IconButton>
        </Tooltip>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: bottomOffset,
        right: 16,
        zIndex: 1100,
        borderRadius: 3,
        minWidth: 260,
        maxWidth: 320,
        overflow: 'hidden',
        transition: 'bottom 0.3s ease',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        WebkitTransform: 'translateZ(0)',
        touchAction: 'manipulation',
        bgcolor: (theme) =>
          theme.palette.mode === 'dark'
            ? 'rgba(30, 30, 30, 0.95)'
            : 'rgba(255, 255, 255, 0.95)',
      }}
    >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Connections
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <IconButton
              disableRipple
              onClick={() => setExpanded(false)}
              aria-label="Minimize"
              sx={{
                p: 1.5,
                minWidth: 44,
                minHeight: 44,
                touchAction: 'manipulation',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
              }}
            >
              <ExpandMoreIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              disableRipple
              onClick={handleDismiss}
              aria-label="Dismiss"
              sx={{
                p: 1.5,
                minWidth: 44,
                minHeight: 44,
                touchAction: 'manipulation',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'rgba(0,0,0,0.1)',
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        </Box>

        {/* Connection rows */}
        <Stack spacing={0}>
          {/* Google */}
          <Collapse in>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.15s',
              }}
            >
              {googleUser ? (
                <>
                  <Avatar
                    src={googleUser.picture}
                    alt={googleUser.name}
                    imgProps={{ referrerPolicy: 'no-referrer' }}
                    sx={{ width: 32, height: 32 }}
                  >
                    <GoogleIcon sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {googleUser.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {googleUser.email}
                    </Typography>
                  </Box>
                  <Tooltip title="Sign out of Google" disableTouchListener>
                    <IconButton onClick={() => { analytics.logoutClicked('google'); googleLogout(); }} aria-label="Sign out of Google" sx={{ p: 1.5, minWidth: 44, minHeight: 44, touchAction: 'manipulation', cursor: 'pointer' }}>
                      <LogoutIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <GoogleIcon sx={{ fontSize: 24 }} />
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      label={googleLoading ? 'Signing in...' : 'Sign in with Google'}
                      size="small"
                      onClick={() => { analytics.loginStarted('google'); googleLogin(); }}
                      disabled={googleLoading}
                      icon={googleLoading ? <CircularProgress size={14} /> : undefined}
                      variant="outlined"
                      sx={{
                        cursor: 'pointer',
                        fontWeight: 500,
                        height: 44,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Collapse>

          {/* Divider */}
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }} />

          {/* Spotify */}
          <Collapse in>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                '&:hover': { bgcolor: 'action.hover' },
                transition: 'background-color 0.15s',
              }}
            >
              {spotifyToken && spotifyUser?.picture ? (
                <Avatar
                  src={spotifyUser.picture}
                  alt={spotifyUser.name}
                  imgProps={{ referrerPolicy: 'no-referrer' }}
                  sx={{ width: 32, height: 32, bgcolor: '#1DB954' }}
                />
              ) : (
                <SpotifyIcon sx={{ fontSize: 24, color: spotifyToken ? '#1DB954' : 'text.secondary' }} />
              )}
              {spotifyToken ? (
                <>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap sx={{ color: '#1DB954' }}>
                      {spotifyUser?.name || 'Spotify Connected'}
                    </Typography>
                    {spotifyUser?.email && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {spotifyUser.email}
                      </Typography>
                    )}
                  </Box>
                  <Tooltip title="Disconnect Spotify" disableTouchListener>
                    <IconButton onClick={() => { analytics.logoutClicked('spotify'); spotifyLogout(); }} aria-label="Disconnect Spotify" sx={{ p: 1.5, minWidth: 44, minHeight: 44, touchAction: 'manipulation', cursor: 'pointer' }}>
                      <LogoutIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <Box sx={{ flex: 1 }}>
                  <Chip
                    label={spotifyLoading ? 'Connecting...' : 'Connect Spotify'}
                    size="small"
                    onClick={() => { analytics.loginStarted('spotify'); spotifyLogin(); }}
                    disabled={spotifyLoading}
                    icon={spotifyLoading ? <CircularProgress size={14} /> : undefined}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 500,
                      height: 44,
                      borderColor: '#1DB954',
                      color: '#1DB954',
                      '&:hover': { bgcolor: 'rgba(29, 185, 84, 0.08)' },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        </Stack>
    </Paper>
  );
}
